import React from 'react';
import { useState, useRef, useEffect, useMemo, forwardRef } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn, filterComboboxOptions } from '../../../utils';
import { Button } from '../Button';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';

// ============================================================================
// Combobox Option Type
// ============================================================================

export interface ComboboxOption {
  /** Unique value for the option */
  value: string;
  /** Display label for the option */
  label: string;
  /** Optional presentation group; ungrouped options appear first. */
  group?: string;
  /** Optional description shown below the label */
  description?: string;
  /** Optional badge to display next to the label (e.g., "Default") */
  badge?: string;
  /** Optional leading glyph, rendered before the label in the list and in the trigger */
  icon?: React.ReactNode;
}

// ============================================================================
// Combobox Props
// ============================================================================

export interface ComboboxProps {
  /** Available options to select from */
  options: ComboboxOption[];
  /** Currently selected value */
  value?: string;
  /** Callback when value changes */
  onValueChange: (value: string) => void;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  /** Text shown when no results match the search */
  emptyText?: string;
  /** Additional class for the popover content */
  className?: string;
  /** Additional class for the trigger button */
  triggerClassName?: string;
  /** Whether the combobox is disabled */
  disabled?: boolean;
}

// ============================================================================
// Combobox Component
// ============================================================================

export const Combobox = forwardRef<HTMLButtonElement, ComboboxProps>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = 'Select option...',
      searchPlaceholder = 'Search...',
      emptyText = 'No results found.',
      className,
      triggerClassName,
      disabled = false,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    // Shared L1 matcher: diacritic-folded, punctuation-insensitive,
    // typo-tolerant, relevance-ranked — identical to table search.
    const filteredOptions = useMemo(
      () => filterComboboxOptions(options, search),
      [options, search]
    );

    const optionGroups = useMemo(() => {
      const ungrouped: ComboboxOption[] = [];
      const groups = new Map<string, ComboboxOption[]>();
      for (const option of filteredOptions) {
        if (!option.group) {
          ungrouped.push(option);
        } else {
          const group = groups.get(option.group) ?? [];
          group.push(option);
          groups.set(option.group, group);
        }
      }
      return [
        { label: undefined, options: ungrouped },
        ...Array.from(groups, ([label, options]) => ({ label, options })),
      ];
    }, [filteredOptions]);

    const selectedOption = options.find((option) => option.value === value);

    // Focus input when popover opens
    useEffect(() => {
      if (open) {
        setTimeout(() => inputRef.current?.focus(), 0);
      } else {
        setSearch('');
      }
    }, [open]);

    const renderOption = (option: ComboboxOption) => (
      <div
        key={option.value}
        onClick={() => {
          onValueChange(option.value);
          setOpen(false);
        }}
        className={cn(
          'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
          'hover:bg-surface-hover hover:text-text-primary',
          value === option.value &&
            'bg-[var(--surface-selected)] text-text-primary'
        )}
      >
        <Check
          className={cn(
            'mr-2 h-4 w-4 shrink-0',
            value === option.value ? 'opacity-100' : 'opacity-0'
          )}
        />
        {option.icon && (
          <span className="mr-2 inline-flex shrink-0 items-center self-center" data-combobox-icon="">
            {option.icon}
          </span>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-w-0 items-start gap-2">
            <span className="min-w-0 flex-1 break-words leading-5">{option.label}</span>
            {option.badge && (
              <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-text-muted">
                {option.badge}
              </span>
            )}
          </div>
          {option.description && (
            <span className="break-words text-xs text-muted-foreground">
              {option.description}
            </span>
          )}
        </div>
      </div>
    );

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'justify-between text-left font-normal',
              'border-border bg-[color:var(--field-bg)]',
              !value && 'text-muted-foreground',
              triggerClassName
            )}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2 truncate">
              {selectedOption ? (
                <>
                  {selectedOption.icon && (
                    <span className="inline-flex shrink-0 items-center" data-combobox-icon="">
                      {selectedOption.icon}
                    </span>
                  )}
                  <span className="min-w-0 flex-1 truncate">{selectedOption.label}</span>
                  {selectedOption.badge && (
                    <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-text-muted">
                      {selectedOption.badge}
                    </span>
                  )}
                </>
              ) : (
                placeholder
              )}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn('w-[var(--radix-popover-trigger-width)] p-0', className)}
          align="start"
        >
          <div className="agn-menu-search-field agn-menu-search-inset flex items-center border-b border-border px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={cn(
                'agn-field-bare',
                'flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none',
                'placeholder:text-muted-foreground',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            />
          </div>
          <div className="max-h-[300px] overflow-y-auto p-1">
            {filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {emptyText}
              </div>
            ) : (
              optionGroups.map(({ label, options: groupOptions }) => label ? (
                <div key={`group:${label}`} role="group" aria-label={label} className="mt-1 border-t border-border">
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{label}</div>
                  {groupOptions.map(renderOption)}
                </div>
              ) : (
                <React.Fragment key="ungrouped">{groupOptions.map(renderOption)}</React.Fragment>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);
Combobox.displayName = 'Combobox';
